import React, { useState, useMemo, memo } from 'react';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { ChevronDown, ChevronRight } from '@saleshandy/icons';
import type {
  IncomingReportTeamRow,
  OutgoingReportTeamRow,
  ReportTeamRow,
} from '../../types';
import type { CallDirectionFilter } from '../reports-sub-header';
import { accessibleOnClick } from '../../../../../../../../shared/utils';
import { DialerSortFields } from '../../../../../../enums/prospect';
import { SortOrder } from '../../../../../../../../shared/enums/order';
import {
  OverlayTooltip,
  OverlayPopover,
  Placement,
} from '../../../../../../../../shared/design-system/components/overlay';
import ProgressCard from '../../../../../../../../shared/components/sequence-progress-card/progress-card';
import ReportsSortableHeader from './components/reports-sortable-header';
import TruncatedTextWithAutoTooltip from '../../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import { formatDuration } from '../../../../utils/helpers';

const columnHelper = createColumnHelper<ReportTeamRow>();

/** Maps IncomingReportTeamRow subRows to ProgressCard AccordionItem format */
const mapTotalCallsBreakdown = (row: IncomingReportTeamRow) => [
  {
    title: 'Total Calls Received',
    score: String(row.totalCallsReceived ?? 0),
  },
  {
    title: 'Direct Incoming Calls',
    score: String(row.directIncomingCalls ?? 0),
  },
  {
    title: 'Forwarded Incoming Calls',
    score: String(row.forwardedIncomingCalls ?? 0),
  },
  //
];

const TotalCallsReceivedCell: React.FC<{ row: IncomingReportTeamRow }> = memo(
  ({ row }) => {
    const [showPopover, setShowPopover] = useState(false);
    const hasBreakdown = (row.subRows?.length ?? 0) > 0;
    const value = row.totalCallsReceived?.toLocaleString() ?? '-';

    const breakdownData = useMemo(
      () => (hasBreakdown ? mapTotalCallsBreakdown(row) : []),
      [hasBreakdown, row],
    );

    if (!hasBreakdown) {
      return <span className="regular-2 gray-txt-17">{value}</span>;
    }

    return (
      <OverlayPopover
        show={showPopover}
        onToggle={() => setShowPopover(false)}
        placement={Placement.BottomStart}
        rootClose={true}
        className="dialer-reports-breakdown-popover"
        content={
          <ProgressCard
            data={breakdownData}
            borderClassName="#93C5FD"
            className="dialer-reports-breakdown-progress-card"
            handleHideOverlayCard={() => setShowPopover(false)}
          />
        }
      >
        <OverlayTooltip
          text="Click to see breakdown"
          placement={Placement.Bottom}
        >
          <span
            className="regular-2 gray-txt-17 cursor-pointer cell-value"
            {...accessibleOnClick(() => setShowPopover(true))}
          >
            {value}
          </span>
        </OverlayTooltip>
      </OverlayPopover>
    );
  },
);

const renderIncomingCellValue = (
  row: IncomingReportTeamRow,
  columnId: keyof IncomingReportTeamRow,
): React.ReactNode => {
  switch (columnId) {
    case 'teamName':
      return row.teamName;
    case 'totalCallsReceived':
      return row.totalCallsReceived?.toLocaleString() ?? '-';
    case 'callsForwarded':
      return row.callsForwarded?.toLocaleString() ?? '-';
    case 'callsPicked':
      return (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row.callsPicked}
          </span>
          <span className="regular-1 gray-txt-11 ml-1 cell-value-suffix-percentage">
            ({row.callsPickedPercent}%)
          </span>
        </span>
      );
    case 'callsUnanswered':
      return (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row.callsUnanswered}
          </span>
          <span className="regular-1 gray-txt-11 ml-1 cell-value-suffix-percentage">
            ({row.callsUnansweredPercent}%)
          </span>
        </span>
      );
    case 'totalTalkTimeMins':
      return row.totalTalkTimeMins != null ? (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row?.totalTalkTimeMins >= 60
              ? formatDuration(Math.floor(row?.totalTalkTimeMins))
              : Math.floor(row?.totalTalkTimeMins)}
          </span>
          <span className="regular-1 gray-txt-11 cell-value-suffix">
            {row?.totalTalkTimeMins < 60 && 's'}
          </span>
        </span>
      ) : (
        '-'
      );
    case 'avgTalkTimeMins':
      return row.avgTalkTimeMins != null ? (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row?.avgTalkTimeMins >= 60
              ? formatDuration(Math.floor(row?.avgTalkTimeMins))
              : Math.floor(row?.avgTalkTimeMins)}
          </span>
          <span className="regular-1 gray-txt-11 cell-value-suffix">
            {row?.avgTalkTimeMins < 60 && 's'}
          </span>
        </span>
      ) : (
        '-'
      );
    default:
      return '-';
  }
};

const renderOutgoingCellValue = (
  row: OutgoingReportTeamRow,
  columnId: keyof OutgoingReportTeamRow,
): React.ReactNode => {
  switch (columnId) {
    case 'teamName':
      return row.teamName;
    case 'totalCallsMade':
      return row.totalCallsMade?.toLocaleString() ?? '-';
    case 'callsConnected':
      return (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row.callsConnected}
          </span>
          <span className="regular-1 gray-txt-11 ml-1 cell-value-suffix-percentage">
            ({row.callsConnectedPercent}%)
          </span>
        </span>
      );
    case 'callsUnanswered':
      return (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row.callsUnanswered}
          </span>
          <span className="regular-1 gray-txt-11 ml-1 cell-value-suffix-percentage">
            ({row.callsUnansweredPercent}%)
          </span>
        </span>
      );
    case 'totalTalkTimeMins':
      return row.totalTalkTimeMins != null ? (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row?.totalTalkTimeMins >= 60
              ? formatDuration(Math.floor(row?.totalTalkTimeMins))
              : Math.floor(row?.totalTalkTimeMins)}
          </span>
          <span className="regular-1 gray-txt-11 cell-value-suffix">
            {row?.totalTalkTimeMins < 60 && 's'}
          </span>
        </span>
      ) : (
        '-'
      );
    case 'avgTalkTimeMins':
      return row.avgTalkTimeMins != null ? (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row?.avgTalkTimeMins >= 60
              ? formatDuration(Math.floor(row?.avgTalkTimeMins))
              : Math.floor(row?.avgTalkTimeMins)}
          </span>
          <span className="regular-1 gray-txt-11 cell-value-suffix">
            {row?.avgTalkTimeMins < 60 && 's'}
          </span>
        </span>
      ) : (
        '-'
      );
    case 'avgRingDurationSecs':
      return row.avgRingDurationSecs != null ? (
        <span>
          <span className="regular-2 gray-txt-17 cell-value">
            {row?.avgRingDurationSecs >= 60
              ? formatDuration(Math.floor(row?.avgRingDurationSecs))
              : Math.floor(row?.avgRingDurationSecs)}
          </span>
          <span className="regular-1 gray-txt-11 cell-value-suffix">
            {row?.avgRingDurationSecs < 60 && 's'}
          </span>
        </span>
      ) : (
        '-'
      );
    default:
      return '-';
  }
};

export interface GetReportsColumnDefinitionsParams {
  /** Include expand column for accordion (team) mode */
  variant?: 'accordion' | 'flat';
  sort?: { sortBy: string; order: SortOrder } | null;
  onSort?: ((columnId: string, order: SortOrder) => void) | null;
  callDirection: CallDirectionFilter;
}

export function getReportsColumnDefinitions({
  variant = 'flat',
  sort = null,
  onSort = null,
  callDirection,
}: GetReportsColumnDefinitionsParams): ColumnDef<ReportTeamRow, any>[] {
  const teamNameColumn = columnHelper.accessor('teamName', {
    id: 'teamName',
    header: variant === 'accordion' ? 'Team Name' : 'User',
    cell: ({ row }) => (
      <div
        className={`d-flex align-items-center w-100 ${
          variant === 'flat' ? 'dialer-reports-member-name' : ''
        }`}
        {...accessibleOnClick(
          row.getCanExpand() ? row.getToggleExpandedHandler() : undefined,
        )}
      >
        {variant === 'accordion' && (
          <span className="dialer-reports-expand-icon">
            {variant === 'accordion' &&
              // eslint-disable-next-line no-nested-ternary
              row.getCanExpand() &&
              (row.getIsExpanded() ? (
                <ChevronDown width={16} height={16} className="gray-txt-9" />
              ) : (
                <ChevronRight width={16} height={16} className="gray-txt-9" />
              ))}
          </span>
        )}
        <span className="regular-2 gray-txt-17 cell-value cell-value-team-name">
          {/* <TruncatedTextWithAutoTooltip text={row.original.teamName} /> */}
          {row?.original?.teamName}
        </span>
      </div>
    ),
    size: 264,
    minSize: 200,
  });

  const useCustomSortHeader = !!(sort && onSort);

  const incomingColumns: ColumnDef<ReportTeamRow, any>[] = [
    {
      id: 'totalCallsReceived',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={
                  <TruncatedTextWithAutoTooltip text="Total Calls Received" />
                }
                columnId="totalCallsReceived"
                sortBy={DialerSortFields.TotalCallsReceived}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Total Calls Received',
      cell: ({ row }) => (
        <TotalCallsReceivedCell row={row.original as IncomingReportTeamRow} />
      ),
      size: 175,
      minSize: 175,
      meta: {
        sortBy: DialerSortFields.TotalCallsReceived,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'callsForwarded',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Calls Forwarded" />}
                columnId="callsForwarded"
                sortBy={DialerSortFields.ForwardedCallCount}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Calls Forwarded',
      cell: ({ row }) =>
        renderIncomingCellValue(
          row.original as IncomingReportTeamRow,
          'callsForwarded',
        ) as React.ReactNode,
      size: 155,
      minSize: 155,
      meta: {
        sortBy: DialerSortFields.ForwardedCallCount,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'callsPicked',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Calls Picked" />}
                columnId="callsPicked"
                sortBy={DialerSortFields.CallsPicked}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Calls Picked',
      cell: ({ row }) =>
        renderIncomingCellValue(
          row.original as IncomingReportTeamRow,
          'callsPicked',
        ) as React.ReactNode,
      size: 125,
      minSize: 125,
      meta: {
        sortBy: DialerSortFields.CallsPicked,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'callsUnanswered',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Call Unanswered" />}
                columnId="callsUnanswered"
                sortBy={DialerSortFields.CallsUnanswered}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Call Unanswered',
      cell: ({ row }) =>
        renderIncomingCellValue(
          row.original as IncomingReportTeamRow,
          'callsUnanswered',
        ) as React.ReactNode,
      size: 155,
      minSize: 155,
      meta: {
        sortBy: DialerSortFields.CallsUnanswered,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'totalTalkTimeMins',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Total Talk Time" />}
                columnId="totalTalkTimeMins"
                sortBy={DialerSortFields.TotalTalkTime}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Total Talk Time',
      cell: ({ row }) =>
        renderIncomingCellValue(
          row.original as IncomingReportTeamRow,
          'totalTalkTimeMins',
        ) as React.ReactNode,
      size: 140,
      minSize: 100,
      meta: {
        sortBy: DialerSortFields.TotalTalkTime,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'avgTalkTimeMins',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Avg Talk Time" />}
                columnId="avgTalkTimeMins"
                sortBy={DialerSortFields.AvgTalkTime}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Avg Talk Time',
      cell: ({ row }) =>
        renderIncomingCellValue(
          row.original as IncomingReportTeamRow,
          'avgTalkTimeMins',
        ) as React.ReactNode,
      size: 140,
      minSize: 100,
      meta: {
        sortBy: DialerSortFields.AvgTalkTime,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
  ];

  const outgoingColumns: ColumnDef<ReportTeamRow, any>[] = [
    {
      id: 'totalCallsMade',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Total Calls Made" />}
                columnId="totalCallsMade"
                sortBy={DialerSortFields.TotalCallsMade}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Total Calls Made',
      cell: ({ row }) =>
        renderOutgoingCellValue(
          row.original as OutgoingReportTeamRow,
          'totalCallsMade',
        ) as React.ReactNode,
      size: 155,
      minSize: 155,
      meta: {
        sortBy: DialerSortFields.TotalCallsMade,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'callsConnected',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Call Connected" />}
                columnId="callsConnected"
                sortBy={DialerSortFields.CallsConnected}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Call Connected',
      cell: ({ row }) =>
        renderOutgoingCellValue(
          row.original as OutgoingReportTeamRow,
          'callsConnected',
        ) as React.ReactNode,
      size: 155,
      minSize: 155,
      meta: {
        sortBy: DialerSortFields.CallsConnected,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'callsUnanswered',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Call Unanswered" />}
                columnId="callsUnanswered"
                sortBy={DialerSortFields.CallsUnanswered}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Call Unanswered',
      cell: ({ row }) =>
        renderOutgoingCellValue(
          row.original as OutgoingReportTeamRow,
          'callsUnanswered',
        ) as React.ReactNode,
      size: 155,
      minSize: 155,
      meta: {
        sortBy: DialerSortFields.CallsUnanswered,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'totalTalkTimeMins',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Total Talk Time" />}
                columnId="totalTalkTimeMins"
                sortBy={DialerSortFields.TotalTalkTime}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Total Talk Time',
      cell: ({ row }) =>
        renderOutgoingCellValue(
          row.original as OutgoingReportTeamRow,
          'totalTalkTimeMins',
        ) as React.ReactNode,
      size: 140,
      minSize: 100,
      meta: {
        sortBy: DialerSortFields.TotalTalkTime,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'avgTalkTimeMins',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={<TruncatedTextWithAutoTooltip text="Avg Talk Time" />}
                columnId="avgTalkTimeMins"
                sortBy={DialerSortFields.AvgTalkTime}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Avg Talk Time',
      cell: ({ row }) =>
        renderOutgoingCellValue(
          row.original as OutgoingReportTeamRow,
          'avgTalkTimeMins',
        ) as React.ReactNode,
      size: 140,
      minSize: 100,
      meta: {
        sortBy: DialerSortFields.AvgTalkTime,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
    {
      id: 'avgRingDurationSecs',
      header:
        useCustomSortHeader && sort && onSort
          ? () => (
              <ReportsSortableHeader
                label={
                  <TruncatedTextWithAutoTooltip text="Avg Ring Duration" />
                }
                columnId="avgRingDurationSecs"
                sortBy={DialerSortFields.AvgRingDuration}
                sort={sort}
                onSort={onSort}
              />
            )
          : 'Avg Ring Duration',
      cell: ({ row }) =>
        renderOutgoingCellValue(
          row.original as OutgoingReportTeamRow,
          'avgRingDurationSecs',
        ) as React.ReactNode,
      size: 155,
      minSize: 155,
      meta: {
        sortBy: DialerSortFields.AvgRingDuration,
        customSortHeader: useCustomSortHeader,
      } as any,
    },
  ];

  const baseColumns =
    callDirection === 'incoming' ? incomingColumns : outgoingColumns;

  if (variant === 'accordion') {
    return [teamNameColumn, ...baseColumns];
  }

  return [teamNameColumn, ...baseColumns];
}
